import { useState, useEffect, useRef } from 'react';
import { useSitemap } from 'wsm-common-data';
import { useShowMapEvent } from './useShowMapEvent';
import { buildLocationTiles } from '../utils/buildLocationTiles';
import { useLocationsData } from './useLocationsData';

const DEFAULT_RANGE = 50;
const DEFAULT_EXCLUDE_BY_RANGE = true;

export const useLocationSearch = (paginationData) => {
	const [range, setRange] = useState(DEFAULT_RANGE);
	const prevRangeRef = useRef(DEFAULT_RANGE);
	const showMap = useShowMapEvent('showMap', false);

	const {
		locations: allLocationData,
		locationCount,
		error: errorMsg,
		isLoading,
		isReady,
		setSearchParams
	} = useLocationsData();

	// Trigger search when postal code or range changes
	useEffect(() => {
		if (!isReady) {
			return; // Skip if not ready
		}
		if (prevRangeRef.current === range) {
			return; // Skip if range hasn't changed (prevents duplicate searches)
		}
		prevRangeRef.current = range;
		const postalCode = window.DDC.LocationsData.getPostalCode();
		if (postalCode) {
			setSearchParams(postalCode, range, DEFAULT_EXCLUDE_BY_RANGE);
		}
	}, [range, isReady, setSearchParams]);

	const sitemap = useSitemap();
	const rawLocationDetailPageUrl = sitemap.get('LOCATION_DETAILS');
	const locationDetailPagePart = (rawLocationDetailPageUrl || '').slice(
		0,
		-4
	);

	const noResults =
		!isLoading &&
		isReady &&
		(locationCount === 0 ||
			!allLocationData ||
			allLocationData.length === 0);

	const tileData = buildLocationTiles(
		paginationData.paginatedData,
		locationDetailPagePart
	);

	return {
		isLoading,
		errorMsg,
		noResults,
		tileData,
		locationCount,
		allLocationData,
		showMap,
		range,
		setRange,
		// Reset triggers for pagination:
		// - range: user changed distance filter
		// - locationCount: underlying data changed (e.g. search cleared from ws-locations-control)
		// - showMap excluded — it's a display toggle, not a search change
		resetTriggers: [range, locationCount]
	};
};
